import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const FIELDS = [
  ["phone", "Clinic phone number", "Shown in the header, footer, Contact page and booking confirmations."],
  ["whatsapp", "WhatsApp number (optional)", "Shown on the Contact page."],
  ["address_line1", "Address line 1 (building / street)", ""],
  ["address_line2", "Address line 2 (locality / landmark, optional)", ""],
  ["city", "City", ""],
  ["state", "State", ""],
  ["pincode", "PIN code", "Used together with the address to place the Google Map pin."],
  ["hours_weekday", "Weekday hours", "e.g. Mon - Sat: 9:30 AM - 8:30 PM"],
  ["hours_weekend", "Sunday / holiday hours (optional)", ""],
];

export const ClinicDetails = () => {
  const { settings, setSettings } = useSettings();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (settings.phone !== undefined) {
      const { email, ...rest } = settings;
      setForm(rest);
    }
  }, [settings]);

  if (!form) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setSettings(data);
      toast.success("Saved. The website has been updated.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Could not save."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-6 max-w-3xl"
      data-testid="admin-clinic-details"
    >
      <div>
        <h2 className="text-lg font-medium text-slate-900">Contact number & address</h2>
        <p className="text-sm text-slate-500 mt-1">
          Change these once and they update everywhere on the website. The clinic email is fixed as{" "}
          <span className="text-slate-700">{settings.email}</span>.
        </p>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        {FIELDS.map(([key, label, help]) => (
          <div key={key} className="space-y-2">
            <Label htmlFor={`field-${key}`}>{label}</Label>
            <Input
              id={`field-${key}`}
              value={form[key] || ""}
              data-testid={`admin-field-${key}`}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              className="rounded-md h-11"
              required={!label.includes("optional")}
            />
            {help && <p className="text-xs text-slate-400">{help}</p>}
          </div>
        ))}
      </div>

      <div className="border-t border-slate-200 pt-6 space-y-3">
        <div>
          <h3 className="text-base font-medium text-slate-900">Google Maps pin</h3>
          <p className="text-sm text-slate-500 mt-1">
            Leave this blank and the map is placed from the address above. To show the exact pin from your
            Google Business Profile, open Google Maps, search the clinic, then <strong>Share → Embed a map</strong>{" "}
            and paste the link (or the whole <code>&lt;iframe&gt;</code> code) here.
          </p>
          <p className="text-xs text-slate-400 mt-2">
            If the clinic address ever changes: update Google Business Profile first, get re-verified, then
            paste the new embed link here.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="field-map_raw">Google Maps embed link (optional)</Label>
          <Input
            id="field-map_raw"
            value={form.map_raw || ""}
            data-testid="admin-field-map_raw"
            onChange={(e) => setForm({ ...form, map_raw: e.target.value })}
            placeholder="https://www.google.com/maps/embed?pb=..."
            className="rounded-md h-11 font-mono text-xs"
          />
          {settings.map_embed_url ? (
            <p className="text-xs text-emerald-700" data-testid="admin-map-status">
              Using your Google Maps embed link on the Contact page.
            </p>
          ) : settings.map_link ? (
            <p className="text-xs text-amber-700" data-testid="admin-map-status">
              That looks like a normal Maps link, not an embed link. It is being used for the “Get directions”
              button, while the map itself is still placed from the address. For the exact pin, paste the
              Share → Embed a map link.
            </p>
          ) : (
            <p className="text-xs text-slate-400" data-testid="admin-map-status">
              Map is currently placed from the address above.
            </p>
          )}
        </div>
      </div>

      <Button
        type="submit"
        disabled={busy}
        data-testid="admin-settings-save-btn"
        className="h-11 rounded-md bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
      >
        <Save className="w-4 h-4 mr-2" />
        {busy ? "Saving…" : "Save Changes"}
      </Button>
    </form>
  );
};
